import {
  AdyenCheckout,
  AdyenCheckoutError,
  ApplePay,
  Card,
  CoreConfiguration,
  Dropin,
  DropinConfiguration,
  GooglePay,
  ICore,
  PayByBank,
  PaymentFailedData,
  PaymentMethodsConfiguration,
  UIElement,
} from '@adyen/adyen-web'
import '@adyen/adyen-web/styles/adyen.css'
import 'adyen-override.css'
import {useCallback, useEffect, useRef} from 'react'
import addTrackingBreadcrumb, {BreadcrumbLevel, BreadcrumbType} from 'utils/addTrackingBreadcrumb'

declare global {
  interface Window {
    ApplePaySession?: any
  }
}

interface Props {
  clientKey?: string
  session: CoreConfiguration['session']
  environment?: CoreConfiguration['environment']
  amount: number
  currency: string
  countryCode?: string
  locale?: string
  showPayButton?: boolean
  paymentMethodsConfiguration: PaymentMethodsConfiguration
  onReady: () => void
  onPaymentCompleted: (result: any, component: any) => void
  onPaymentFailed: (data: PaymentFailedData, element: UIElement) => void
  onError: (error: AdyenCheckoutError) => void
  onPaymentTypeChange: (paymentType: string) => void
}

export const AdyenDropin = ({
  clientKey,
  session,
  environment = 'test',
  amount,
  currency,
  countryCode = 'US',
  locale = 'en-US',
  showPayButton = true,
  paymentMethodsConfiguration,
  onReady,
  onPaymentCompleted,
  onPaymentFailed,
  onError,
  onPaymentTypeChange,
}: Props) => {
  const dropinContainerRef = useRef<any>()
  const adyenCheckoutRef = useRef<ICore | null>(null)
  const adyenDropinRef = useRef<Dropin | null>(null)
  const isAdyenSetup = useRef<boolean>(false)

  const setup = useCallback(async () => {
    // Global Adyen configuration:
    const adyenConfig: CoreConfiguration = {
      clientKey,
      session,
      environment,
      analytics: {
        enabled: false,
      },
      amount: {
        value: amount,
        currency: currency,
      },
      countryCode,
      locale,
      showPayButton,
      onPaymentCompleted,
      onPaymentFailed,
      onError,
    }

    adyenCheckoutRef.current = await AdyenCheckout(adyenConfig)
    const dropInComponents: any[] = [Card, GooglePay, PayByBank]
    const isLikelyMobileDevice = () => {
      // If the device explicitly identifies as an iOS device:
      if (!!navigator.userAgent.match(/(iPad|iPhone|iPod)/i)) {
        return true
      }

      // If the device supports touch screen:
      if (typeof navigator.maxTouchPoints !== 'undefined' && navigator.maxTouchPoints > 0) {
        return true
      }

      return false
    }

    // For now, we only want Apple Pay to appear on iOS, as it doesn't seem to work on Safari on Mac,
    // and we don't want it showing as the default option on browsers where it doesn't work natively.
    if (!!window.ApplePaySession && isLikelyMobileDevice()) {
      dropInComponents.push(ApplePay)

      console.log('Added Apple Pay')

      addTrackingBreadcrumb({
        type: BreadcrumbType.Default,
        level: BreadcrumbLevel.Info,
        message: 'Natively supports Apple Pay and is likely a mobile device, added Apple Pay payment option.',
      })
    }

    // Drop in configuration:
    const dropinConfig: DropinConfiguration = {
      paymentMethodComponents: dropInComponents,
      paymentMethodsConfiguration,
      onReady,
      disableFinalAnimation: true,

      onSelect(paymentMethod: UIElement) {
        onPaymentTypeChange(paymentMethod.props.name ?? 'unknown')
      },
    }

    if (adyenCheckoutRef.current && dropinContainerRef.current) {
      adyenDropinRef.current = new Dropin(adyenCheckoutRef.current, dropinConfig)
      adyenDropinRef.current.mount(dropinContainerRef.current)
    }
  }, [
    clientKey,
    session,
    environment,
    amount,
    currency,
    countryCode,
    locale,
    showPayButton,
    onPaymentCompleted,
    onPaymentFailed,
    onError,
    onPaymentTypeChange,
    onReady,
    paymentMethodsConfiguration,
  ])

  useEffect(() => {
    return () => {
      if (adyenDropinRef.current) {
        adyenDropinRef.current.unmount()
        adyenDropinRef.current = null
      }

      if (adyenCheckoutRef.current) {
        adyenCheckoutRef.current = null
      }

      isAdyenSetup.current = false
    }
  }, [])

  useEffect(() => {
    if (!isAdyenSetup.current) {
      isAdyenSetup.current = true
      void setup()
    }
  }, [setup])

  return <div id="adyen-container" ref={dropinContainerRef} />
}
